import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/global.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://zippy-flexibility-production.up.railway.app';

const staffApi = axios.create({ baseURL: API_URL });
staffApi.interceptors.request.use(config => {
  const token = localStorage.getItem('staffToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const SESSION_TYPES = ['Sneak Peak Scan', 'Sneaky Peek Scan', 'Sneak A Peek Scan', '2D Gender Scan', '3D Gender Scan', 'Bump-A-Boo Scan'];

export default function StaffDashboard() {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState('todo'); // todo | sent | all
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [files, setFiles] = useState([]);
  const [editFields, setEditFields] = useState({ gestation: '', due_date: '' });
  const [sendEmail, setSendEmail] = useState(true);
  const [toast, setToast] = useState('');
  const [doneMsg, setDoneMsg] = useState(''); // shown on the finish button after a successful upload
  const [showUpload, setShowUpload] = useState(false); // hand-upload section hidden by default (rare off-system use)
  const [showAltEmail, setShowAltEmail] = useState(false); // "send to a different email" option
  const [altEmail, setAltEmail] = useState('');
  const emptyAdd = { first_name: '', last_name: '', email: '', phone: '', session_date: '', session_type: '', gestation: '', due_date: '' };
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addFields, setAddFields] = useState(emptyAdd);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const res = await staffApi.get('/staff/sessions');
      setSessions(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/staff/login');
    } finally { setLoading(false); }
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await staffApi.post('/staff/sync');
      showToast(res.data.message);
      loadSessions();
    } catch { showToast('Sync failed. Check your Square connection.'); }
    finally { setSyncing(false); }
  };

  const handleUpload = async () => {
    if (!files.length) { showToast('Please select files to upload.'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('sendEmail', sendEmail.toString());
      if (editFields.gestation) formData.append('gestation', editFields.gestation);
      if (editFields.due_date) formData.append('due_date', editFields.due_date);
      await staffApi.post(`/staff/sessions/${selected.id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const n = files.length;
      showToast(`${n} file${n > 1 ? 's' : ''} uploaded successfully!`);
      setFiles([]);
      // Keep the box open showing a clear finish button instead of vanishing.
      setDoneMsg(sendEmail ? '✓ Sent to client — Done' : '✓ Uploaded — Done');
      loadSessions();
    } catch { showToast('Upload failed. Please try again.'); }
    finally { setUploading(false); }
  };

  const handleSendGalleryEmail = async (email) => {
    setSendingEmail(true);
    try {
      const res = await staffApi.post(`/staff/sessions/${selected.id}/send-notification`, email ? { email } : {});
      showToast(res.data.message || 'Gallery email sent!');
      setSelected(s => (s ? { ...s, notification_sent: true } : s));
      if (email) { setAltEmail(''); setShowAltEmail(false); }
      loadSessions();
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not send gallery email.');
    } finally { setSendingEmail(false); }
  };

  const handleSaveFields = async () => {
    await staffApi.put(`/staff/sessions/${selected.id}`, editFields);
    showToast('Session updated!');
    loadSessions();
  };

  const handleArchive = async () => {
    await staffApi.post(`/staff/sessions/${selected.id}/archive`);
    showToast('Moved to Archived');
    setSelected(null);
    loadSessions();
  };

  const handleRestore = async () => {
    await staffApi.post(`/staff/sessions/${selected.id}/restore`);
    showToast('Restored to To Do');
    setSelected(null);
    loadSessions();
  };

  const handleArchiveAll = async () => {
    if (!window.confirm('Archive all sessions currently in To Do? They’ll move to the Archived tab and can be restored anytime.')) return;
    const res = await staffApi.post('/staff/sessions/archive-todo');
    showToast(res.data.message || 'Archived');
    loadSessions();
  };

  const handleAddClient = async () => {
    if (!addFields.first_name.trim() || !addFields.last_name.trim() || !addFields.email.trim() || !addFields.session_date) {
      showToast('Please fill in first name, last name, email and session date.');
      return;
    }
    setAdding(true);
    try {
      await staffApi.post('/staff/clients', addFields);
      showToast('Client added — they’re now in To Do.');
      setShowAdd(false);
      setAddFields(emptyAdd);
      loadSessions();
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not add client.');
    } finally { setAdding(false); }
  };

  const logout = () => { localStorage.removeItem('staffToken'); navigate('/staff/login'); };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

  let filtered = sessions.filter(s => {
    // "To Do" = still needs work: not sent to the client yet, and not cancelled.
    if (filter === 'todo' && (s.notification_sent || s.status === 'cancelled' || s.status === 'archived')) return false;
    if (filter === 'sent' && !s.notification_sent) return false;
    if (filter === 'archived' && s.status !== 'archived') return false;
    // 'all' shows everything
    if (search && !`${s.first_name} ${s.last_name} ${s.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // On the Sent tab, show the most recently sent at the top.
  if (filter === 'sent') {
    filtered = [...filtered].sort((a, b) => new Date(b.sent_at || b.updated_at || 0) - new Date(a.sent_at || a.updated_at || 0));
  }

  const stats = {
    awaiting: sessions.filter(s => s.status === 'pending').length,
    // Only count as ready-to-send once the upload has fully finished.
    toSend: sessions.filter(s => s.status === 'ready' && !s.notification_sent && !s.uploading).length,
    sent: sessions.filter(s => s.notification_sent).length
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f7f7' }}>
      {/* Header */}
      <header className="app-header">
        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal)' }}>
          BUMP <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', marginLeft: '12px', letterSpacing: '1px', textTransform: 'uppercase' }}>Staff</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={() => { setAddFields(emptyAdd); setShowAdd(true); }} style={{ background: 'var(--coral)', color: 'white', border: 'none', borderRadius: '100px', padding: '7px 16px', fontSize: '12px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>
            + Add Client
          </button>
          <button onClick={handleSync} disabled={syncing} style={{ background: syncing ? 'var(--border)' : 'var(--teal-light)', color: 'var(--teal)', border: '2px solid var(--border)', borderRadius: '100px', padding: '7px 16px', fontSize: '12px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>
            {syncing ? 'Syncing...' : '↻ Sync Square'}
          </button>
          <button onClick={logout} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '100px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>Log out</button>
        </div>
      </header>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '28px 24px', width: '100%', flex: 1 }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' }}>
          {[
            { label: 'Awaiting Upload', value: stats.awaiting, color: 'var(--coral)' },
            { label: 'Ready to Send', value: stats.toSend, color: 'var(--teal-dark)' },
            { label: 'Sent', value: stats.sent, color: 'var(--teal)' }
          ].map(stat => (
            <div key={stat.label} style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid var(--border)', boxShadow: '0 2px 10px rgba(83,147,144,0.07)' }}>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '32px', color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search & Filter */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '10px 16px', border: '2px solid var(--border)', borderRadius: '100px', fontSize: '14px', color: 'var(--text)', outline: 'none', fontFamily: 'Nunito, sans-serif' }}
          />
          {['todo', 'sent', 'archived', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ background: filter === f ? 'var(--teal)' : 'white', color: filter === f ? 'white' : 'var(--text-soft)', border: '2px solid var(--border)', borderRadius: '100px', padding: '9px 20px', fontSize: '13px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>
              {f === 'todo' ? 'To Do' : f === 'sent' ? 'Sent' : f === 'archived' ? 'Archived' : 'All'}
            </button>
          ))}
          {filter === 'todo' && filtered.length > 0 && (
            <button onClick={handleArchiveAll} style={{ marginLeft: 'auto', background: 'white', color: 'var(--text-soft)', border: '2px solid var(--border)', borderRadius: '100px', padding: '9px 18px', fontSize: '13px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>
              Archive all
            </button>
          )}
        </div>

        {/* Sessions Table */}
        <div style={{ background: 'white', borderRadius: '18px', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: '0 2px 12px rgba(83,147,144,0.08)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-soft)' }}>Loading sessions...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-soft)' }}>No sessions found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--teal-light)', borderBottom: '2px solid var(--border)' }}>
                  {['Client', 'Session Date', 'Session Type', 'Status', 'Files', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-soft)', fontFamily: 'Nunito, sans-serif' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((session, i) => (
                  <tr key={session.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--teal-light)' : 'none' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--teal-dark)', fontFamily: 'Nunito, sans-serif' }}>{session.first_name} {session.last_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>{session.email}</div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text)', fontWeight: 600 }}>{formatDate(session.session_date)}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text)' }}>{session.session_type || '—'}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span className={`pill ${session.uploading || session.status === 'pending' ? 'pill-coral' : (session.status === 'cancelled' || session.status === 'archived') ? '' : 'pill-teal'}`} style={(session.status === 'cancelled' || session.status === 'archived') ? { background: '#ececec', color: '#9a9a9a' } : undefined}>
                        {session.uploading ? 'Uploading…' : session.status === 'pending' ? 'Awaiting Upload' : session.status === 'cancelled' ? 'Cancelled' : session.status === 'archived' ? 'Archived' : session.notification_sent ? 'Sent' : 'Ready'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-soft)', fontWeight: session.uploading ? 700 : 400 }}>{session.uploading ? `${session.file_count || 0}/${session.upload_expected || '?'}` : (session.file_count || 0)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <button onClick={() => { setSelected(session); setFiles([]); setDoneMsg(''); setShowUpload(false); setShowAltEmail(false); setAltEmail(''); setEditFields({ gestation: session.gestation || '', due_date: session.due_date?.split('T')[0] || '' }); }} style={{ background: session.status === 'pending' ? 'var(--coral)' : 'var(--teal-light)', color: session.status === 'pending' ? 'white' : 'var(--teal)', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(45,100,100,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div className="card" style={{ maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal-dark)', marginBottom: '4px' }}>
              {selected.first_name} {selected.last_name}
            </h3>
            <div style={{ fontSize: '13px', color: 'var(--text-soft)', marginBottom: '14px' }}>
              {selected.session_type} · {formatDate(selected.session_date)} · {selected.email}
            </div>
            <div style={{ marginBottom: '22px' }}>
              {selected.status === 'archived' ? (
                <button onClick={handleRestore} style={{ background: 'var(--teal-light)', border: 'none', color: 'var(--teal)', fontSize: '13px', fontWeight: 700, cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', fontFamily: 'Nunito, sans-serif' }}>↩ Restore to To Do</button>
              ) : (
                <button onClick={handleArchive} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', padding: '7px 14px', borderRadius: '8px', fontFamily: 'Nunito, sans-serif' }}>Archive (set aside)</button>
              )}
            </div>

            {/* Session Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Gestation</label>
                <select value={editFields.gestation} onChange={e => setEditFields(f => ({ ...f, gestation: e.target.value }))}>
                  <option value="">Select weeks</option>
                  {Array.from({ length: 31 }, (_, i) => i + 10).map(w => <option key={w} value={`${w} weeks`}>{w} weeks</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Due Date</label>
                <input type="date" value={editFields.due_date} onChange={e => setEditFields(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <button onClick={handleSaveFields} style={{ background: 'var(--teal-light)', color: 'var(--teal)', border: '2px solid var(--border)', borderRadius: '10px', padding: '9px 20px', fontSize: '13px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', marginBottom: '20px' }}>Save Details</button>

            {/* SEND — the one clear action, always in the same place. */}
            {(() => {
              const count = Number(selected.file_count) || 0;
              const expected = Number(selected.upload_expected) || 0;
              const sent = selected.notification_sent;
              const uploading = !!selected.uploading;                 // files still arriving
              const ready = !uploading && (count > 0 || selected.status === 'ready'); // safe to send
              const blocked = !ready && !sent;                         // can't send yet (uploading or waiting)
              return (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '20px', marginBottom: '18px' }}>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '13px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-soft)', marginBottom: '10px' }}>Send Gallery</div>
                  <div style={{ fontSize: '13px', color: uploading ? 'var(--coral)' : 'var(--text-soft)', marginBottom: '12px', lineHeight: 1.5, fontWeight: uploading ? 700 : 400 }}>
                    {sent
                      ? `✓ The gallery email has been sent to ${selected.first_name}.`
                      : uploading
                        ? `⏳ Still uploading ${selected.first_name}'s photos — ${count}${expected ? ` of ${expected}` : ''} in so far. Please wait until they're all in before sending.`
                        : ready
                          ? `${selected.first_name}'s scans are all in${count ? ` (${count} files)` : ''}. Check the gallery looks right, then send.`
                          : `Waiting for ${selected.first_name}'s scans to come in from the machine. This button lights up as soon as they land — nothing to do until then.`}
                  </div>
                  {selected.gallery_link && (
                    <a href={selected.gallery_link} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', background: 'white', color: 'var(--teal)', border: '2px solid var(--border)', borderRadius: '12px', padding: '12px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', marginBottom: '10px' }}>
                      👁  View gallery (what the client sees)
                    </a>
                  )}
                  <button
                    onClick={() => handleSendGalleryEmail()}
                    disabled={sendingEmail || blocked}
                    style={{
                      width: '100%',
                      background: blocked ? 'var(--border)' : sent ? 'var(--teal-light)' : 'var(--teal)',
                      color: blocked ? 'var(--text-soft)' : sent ? 'var(--teal)' : 'white',
                      border: 'none', borderRadius: '12px', padding: '14px', fontSize: '15px', fontWeight: 800,
                      fontFamily: 'Nunito, sans-serif',
                      cursor: (sendingEmail || blocked) ? 'default' : 'pointer',
                    }}>
                    {sendingEmail ? 'Sending…' : sent ? 'Resend gallery email' : uploading ? `Uploading… ${count}${expected ? `/${expected}` : ''}` : ready ? 'Send gallery to client' : 'Waiting for scans…'}
                  </button>

                  {/* Send to a different email (e.g. the saved one bounced / went to spam) — one-off, doesn't change their saved email. */}
                  {(ready || sent) && (
                    <div style={{ marginTop: '12px' }}>
                      {!showAltEmail ? (
                        <button onClick={() => setShowAltEmail(true)} style={{ background: 'none', border: 'none', color: 'var(--text-soft)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', textDecoration: 'underline', padding: 0 }}>
                          ✉  Send to a different email address
                        </button>
                      ) : (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginBottom: '7px', lineHeight: 1.4 }}>
                            One-off send to another address — doesn’t change their saved email ({selected.email}).
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="email" value={altEmail} onChange={e => setAltEmail(e.target.value)} placeholder="their other email address"
                              style={{ flex: 1, minWidth: '170px', padding: '10px 14px', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', color: 'var(--text)', outline: 'none', fontFamily: 'Nunito, sans-serif' }} />
                            <button onClick={() => handleSendGalleryEmail(altEmail.trim())} disabled={sendingEmail || !altEmail.trim()}
                              style={{ background: altEmail.trim() ? 'var(--teal)' : 'var(--border)', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 16px', fontSize: '13px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: altEmail.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
                              Send here
                            </button>
                            <button onClick={() => { setShowAltEmail(false); setAltEmail(''); }}
                              style={{ background: 'none', border: 'none', color: 'var(--text-soft)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Rare: upload files by hand (off-system client). Hidden by default so it doesn't clutter the normal flow. */}
            <div style={{ marginBottom: '18px' }}>
              {!showUpload ? (
                <button onClick={() => setShowUpload(true)} style={{ background: 'none', border: 'none', color: 'var(--text-soft)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', textDecoration: 'underline', padding: 0 }}>
                  + Upload files by hand
                </button>
              ) : (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '13px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-soft)' }}>Upload files by hand</div>
                    <button onClick={() => { setShowUpload(false); setFiles([]); }} style={{ background: 'none', border: 'none', color: 'var(--text-soft)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>Hide</button>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginBottom: '12px', lineHeight: 1.5 }}>Only for an off-system client whose scans didn’t come from the machine.</div>
                  <div
                    style={{ border: '2px dashed var(--border)', borderRadius: '14px', padding: '24px', textAlign: 'center', cursor: 'pointer', background: files.length ? 'var(--teal-light)' : 'transparent' }}
                    onClick={() => document.getElementById('fileInput').click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)); }}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '6px' }}>📁</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--teal-dark)', fontFamily: 'Nunito, sans-serif' }}>
                      {files.length ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : 'Drop files here or click to browse'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '4px' }}>MP4, JPG, PNG, JPEG — up to 500MB per file</div>
                    <input id="fileInput" type="file" multiple accept="video/*,image/*" style={{ display: 'none' }} onChange={e => setFiles(Array.from(e.target.files))} />
                  </div>
                  {files.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      {files.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--teal-light)', borderRadius: '8px', marginBottom: '6px', fontSize: '12px', color: 'var(--teal-dark)' }}>
                          <span style={{ fontWeight: 600 }}>{f.name}</span>
                          <span style={{ color: 'var(--text-soft)' }}>{Math.round(f.size / 1024 / 1024)} MB</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px', padding: '12px', background: 'var(--teal-light)', borderRadius: '10px' }}>
                    <div onClick={() => setSendEmail(!sendEmail)} style={{ width: '20px', height: '20px', border: `2px solid ${sendEmail ? 'var(--teal)' : 'var(--border)'}`, borderRadius: '5px', background: sendEmail ? 'var(--teal)' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {sendEmail && <span style={{ color: 'white', fontSize: '12px', fontWeight: 800 }}>✓</span>}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--teal-dark)', fontFamily: 'Nunito, sans-serif' }}>Also send the gallery email now</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-soft)' }}>Emails the client their gallery link once these files finish uploading</div>
                    </div>
                  </div>
                  <button onClick={handleUpload} disabled={uploading || !files.length} style={{ width: '100%', marginTop: '12px', background: files.length ? 'var(--coral)' : 'var(--border)', color: 'white', border: 'none', borderRadius: '12px', padding: '13px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: files.length ? 'pointer' : 'not-allowed' }}>
                    {uploading ? 'Uploading…' : `Upload ${files.length ? `${files.length} file${files.length > 1 ? 's' : ''}` : 'files'}`}
                  </button>
                </div>
              )}
            </div>

            {/* Close */}
            <button onClick={() => { setSelected(null); setFiles([]); setDoneMsg(''); setShowUpload(false); setShowAltEmail(false); setAltEmail(''); }} style={{ width: '100%', background: doneMsg ? 'var(--teal)' : 'var(--teal-light)', color: doneMsg ? 'white' : 'var(--teal)', border: doneMsg ? 'none' : '2px solid var(--border)', borderRadius: '12px', padding: '13px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>
              {doneMsg || 'Done'}
            </button>
          </div>
        </div>
      )}

      {/* Add Client Modal (off-system bookings) */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(45,100,100,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div className="card" style={{ maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal-dark)', marginBottom: '4px' }}>Add a client</h3>
            <div style={{ fontSize: '13px', color: 'var(--text-soft)', marginBottom: '20px', lineHeight: 1.5 }}>
              For an off-system booking (a walk-in, or booked outside Acuity). They’ll appear in <b>To Do</b> so you can upload their scans and send their gallery just like any other client.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group"><label>First name *</label>
                <input value={addFields.first_name} onChange={e => setAddFields(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" />
              </div>
              <div className="form-group"><label>Last name *</label>
                <input value={addFields.last_name} onChange={e => setAddFields(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" />
              </div>
            </div>

            <div className="form-group"><label>Email *</label>
              <input type="email" value={addFields.email} onChange={e => setAddFields(f => ({ ...f, email: e.target.value }))} placeholder="client@email.com" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group"><label>Phone</label>
                <input value={addFields.phone} onChange={e => setAddFields(f => ({ ...f, phone: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="form-group"><label>Session date *</label>
                <input type="date" value={addFields.session_date} onChange={e => setAddFields(f => ({ ...f, session_date: e.target.value }))} />
              </div>
            </div>

            <div className="form-group"><label>Session type</label>
              <input list="sessionTypes" placeholder="e.g. 2D Gender Scan" value={addFields.session_type} onChange={e => setAddFields(f => ({ ...f, session_type: e.target.value }))} />
              <datalist id="sessionTypes">{SESSION_TYPES.map(t => <option key={t} value={t} />)}</datalist>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
              <div className="form-group"><label>Gestation</label>
                <select value={addFields.gestation} onChange={e => setAddFields(f => ({ ...f, gestation: e.target.value }))}>
                  <option value="">Select weeks</option>
                  {Array.from({ length: 31 }, (_, i) => i + 10).map(w => <option key={w} value={`${w} weeks`}>{w} weeks</option>)}
                </select>
              </div>
              <div className="form-group"><label>Due date</label>
                <input type="date" value={addFields.due_date} onChange={e => setAddFields(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button onClick={() => { setShowAdd(false); setAddFields(emptyAdd); }} style={{ flex: 1, background: 'var(--teal-light)', color: 'var(--teal)', border: '2px solid var(--border)', borderRadius: '12px', padding: '13px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddClient} disabled={adding} style={{ flex: 2, background: 'var(--coral)', color: 'white', border: 'none', borderRadius: '12px', padding: '13px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: adding ? 'default' : 'pointer' }}>
                {adding ? 'Adding...' : 'Add Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: 'var(--teal-dark)', color: 'white', padding: '12px 24px', borderRadius: '100px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', boxShadow: '0 8px 24px rgba(45,100,100,0.3)', zIndex: 300 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
